import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import PropTypes from 'prop-types';

import 'react-image-crop/dist/ReactCrop.css';
import { v1 as uuid } from 'uuid';
import { Button, ButtonToolbar, Modal } from 'reactstrap';
import { canvasPreview } from './components/CanvasPreview';

export const useDebounceEffect = (fn, waitTime, deps) => {
  useEffect(() => {
    const t = setTimeout(() => {
      fn(undefined, ...deps);
    }, waitTime);

    return () => {
      clearTimeout(t);
    };
  }, deps);
};

function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

const ImageCrop = forwardRef(({ t, onAcceptedFile }, ref) => {
  const [imgSrc, setImgSrc] = useState('');
  const [isOpenModal, setIsOpenModal] = useState(false);
  const previewCanvasRef = useRef(null);
  const inputRef = useRef(null);
  const imgRef = useRef(null);
  const [crop, setCrop] = useState({
    unit: '%',
    x: 25,
    y: 25,
    width: 50,
    height: 50,
  });
  const [completedCrop, setCompletedCrop] = useState();

  const toggleIsOpenModal = () => {
    setIsOpenModal(!isOpenModal);
  };

  const onSelectFile = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined);
      const reader = new FileReader();
      reader.addEventListener('load', () =>
        setImgSrc(reader.result.toString() || '')
      );
      reader.readAsDataURL(e.target.files[0]);
      toggleIsOpenModal();
    }
  };

  const createFileCropped = () => {
    toggleIsOpenModal();
    previewCanvasRef.current.toBlob(async (blob) => {
      const file = await new File([blob], 'fileName.jpg', {
        type: 'image/jpeg',
      });
      const fileWithPreview = Object.assign(file, {
        preview: URL.createObjectURL(file),
        id: uuid(),
        uploadType: file.type,
      });
      onAcceptedFile(fileWithPreview);
    }, 'image/jpeg');
  };

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 16 / 9));
  };

  const openFileBrowser = () => {
    inputRef.current.click();
  };

  useDebounceEffect(
    async () => {
      if (
        completedCrop &&
        completedCrop.width &&
        completedCrop.height &&
        imgRef.current &&
        previewCanvasRef.current
      ) {
        canvasPreview(imgRef.current, previewCanvasRef.current, completedCrop);
      }
    },
    100,
    [completedCrop]
  );

  useImperativeHandle(ref, () => ({
    openFileBrowser,
  }));

  return (
    <div className="image-crop">
      <div className="image-crop-control">
        <input
          ref={inputRef}
          type="file"
          accept="image/*"
          onChange={onSelectFile}
        />
      </div>
      <Modal isOpen={isOpenModal}>
        <div className="mt-2 bg-dark">
          {Boolean(imgSrc) && (
            <ReactCrop
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={(c) => setCompletedCrop(c)}
              keepSelection
              ruleOfThirds
            >
              <img
                className="image-crop-img"
                ref={imgRef}
                alt="Crop me"
                src={imgSrc}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          )}
        </div>
        <ButtonToolbar className="d-flex justify-content-center">
          <Button color="danger" onClick={toggleIsOpenModal}>
            {t('general.cancel')}
          </Button>
          <Button color="success" onClick={createFileCropped}>
            {t('general.confirm')}
          </Button>
        </ButtonToolbar>
      </Modal>
      <div>
        {Boolean(completedCrop) && (
          <canvas
            ref={previewCanvasRef}
            style={{
              display: 'none',
              objectFit: 'contain',
              width: completedCrop.width,
              height: completedCrop.height,
            }}
          />
        )}
      </div>
    </div>
  );
});

ImageCrop.propTypes = {
  t: PropTypes.func.isRequired,
  onAcceptedFile: PropTypes.func.isRequired,
};

export default ImageCrop;
